import { CommonModule, DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, signal, viewChild, inject } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ButtonComponent } from '../../../../../shared/components/button/button.component';
import { NoRecordsComponent } from '../../../../../shared/components/no-records/no-records.component';
import { TagComponent } from '../../../../../shared/components/tag/tag.component';
import { CollapsibleDirective } from '../../../../../shared/directives/collapsibleDirective.directive';
import { ActionType, TableColumnsType } from '../../../../../shared/enums/common';
import { DriverDocument } from '../../../../../shared/interfaces/driver-firestore';
import { FireStoreService } from '../../../../../shared/services/firestore.service';
import { RequestBuilderService } from '../../../../../shared/services/request-builder.service';
import { StateService } from '../../../../../shared/services/state.service';
import { DocumentData } from 'firebase/firestore';
import { MenuItem, MessageService } from 'primeng/api';
import { SelectModule } from 'primeng/select';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { MenuModule } from 'primeng/menu';
import { SelectButtonModule } from 'primeng/selectbutton';
import { Table, TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { CameraStatus, CameraStatusService } from '../../../services/camera-status.service';
import { CameraFilterUtils, CameraFilterOption } from '../../../services/camera-filter-utils.service';
import { Subscription } from 'rxjs';
import { Driver, DriverFirestoreLocation } from '../../../interfaces/driver';
import { ActionDialogService } from '../../../services/action-dialog.service';
import { DriversMapComponent } from '../../drivers-map/drivers-map.component';
import { EnvConfigService } from '../../../../../shared/services/env-config.service';
import { RideActionsService } from '../../../services/ride-actions.service';
import { UserRole } from '../../../../../core/auth/enums/userRoles';

interface DriverOptionsData {
  name: string;
  partnerId: number;
  mobileNumber?: string;
  organization?: string;
  cameraStatus?: CameraStatus | null;
}

type coords = { longitude: number; latitude: number };
@Component({
  selector: 'app-find-driver-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonComponent, InputNumberModule, SelectModule, CollapsibleDirective, SelectButtonModule, TableModule, TagComponent, TooltipModule, ReactiveFormsModule, MenuModule, DriversMapComponent, NoRecordsComponent],
  templateUrl: './find-driver-dialog.component.html',
  styleUrl: './find-driver-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FindDriverDialogComponent implements OnInit, OnDestroy {
  ref = inject(DynamicDialogRef);
  config = inject(DynamicDialogConfig);
  private readonly requestBuilder = inject(RequestBuilderService);
  private readonly fb = inject(FormBuilder);
  private readonly dialogService = inject(DialogService);
  private readonly messageService = inject(MessageService);
  private readonly firestoreService = inject(FireStoreService);
  private readonly stateService = inject(StateService);
  private readonly datePipe = inject(DatePipe);
  private readonly actionDialogService = inject(ActionDialogService);
  private readonly env = inject(EnvConfigService);
  private readonly rideActionsService = inject(RideActionsService);
  private readonly cameraStatusService = inject(CameraStatusService);

  showAdvancedFilters = false;
  filtersForm!: FormGroup;
  loading = false;
  viewsOptions = [
    { id: 1, name: 'List', icon: 'icon-list-bullets' },
    { id: 2, name: 'Map', icon: 'icon-map-trifold' },
  ];
  selectedView = 1;

  allDrivers = signal<Driver[]>([]);
  filteredDrivers = signal<Driver[]>([]);

  driversMap = new Map();
  filteredDriversMap = new Map();

  subdriverAssignedSub!: Subscription;

  filtersControls: any = [];
  advancedFiltersControls: any = [];
  allDriversOptions: DriverOptionsData[] = [];
  driversOptions = signal<DriverOptionsData[]>([]);
  driverFirestoreLocations = signal<DriverFirestoreLocation[]>([]);
  updatedDriverLocation = signal<DriverFirestoreLocation | null>(null);
  firestoreDriversInitialLoad = true;
  tableColumnsType = TableColumnsType;

  data: any[] = [];
  rows = 10;
  first = 0;
  totalRecords = 0;
  columns: any[] = [];

  menuItems = signal<MenuItem[] | undefined>([]);

  currentRole = (localStorage.getItem('role') ?? '').toLowerCase();
  private readonly cameraSubscriptions: Subscription[] = [];
  currentFilterCode = 'all'; // Track current active filter
  cameraStatusFilters: CameraFilterOption[] = [...CameraFilterUtils.DEFAULT_FILTER_OPTIONS];

  /**
   * Updates the camera status filter and applies incremental filtering
   */
  updateCameraStatusFilter(filterCode: string): void {
    this.cameraStatusFilters = CameraFilterUtils.updateFilterSelection(this.cameraStatusFilters, filterCode);
    this.currentFilterCode = filterCode;
    this.applyCameraStatusFilter(filterCode);
  }

  /**
   * Applies camera status filter to the driver options
   */
  private applyCameraStatusFilter(filterCode: string): void {
    const filtered = CameraFilterUtils.filterDrivers(this.allDriversOptions, filterCode);
    this.driversOptions.set(filtered);
  }

  setMenu(e: any, selectedDriver: Driver, menuRef: any) {
    this.menuItems.set([
      {
        label: 'Assign to ride',
        command: e => {
          this.openAssignSubDriverDialog(selectedDriver.partnerId);
        },
        disabled: selectedDriver.bestMatch <= 50,
      },
      {
        label: 'Hide from search result',
        command: e => {
          this.hideDriverFromResults(selectedDriver);
        },
      },
    ]);
    menuRef.toggle(e);
  }

  rowsPerPageOptions: number[] = [5, 10, 25, 50, 100];
  activityValues: number[] = [0, 100];
  filterValue = '';
  readonly table = viewChild<Table>('table');
  sub!: Subscription;

  constructor() {
    const dateOfService = typeof this.config.data.rideDateOfService == 'string' ? new Intl.DateTimeFormat('en-US').format(new Date(this.config.data.rideDateOfService)) : this.config.data.rideDateOfService.toDate();

    this.filtersControls = [
      { controlName: 'fullfilmentType', label: 'Fullfilment Type', isReadonly: true, type: 'dropdown', initialValue: { name: 'Ride Cover' }, options: [{ name: 'Ride Cover' }] },
      { controlName: 'duration', label: 'Duration', isReadonly: true, type: 'dropdown', initialValue: { name: this.datePipe.transform(dateOfService, 'MM/dd/y') }, options: [{ name: this.datePipe.transform(dateOfService, 'MM/dd/y') }] },
      { controlName: 'driver', label: 'Driver', isReadonly: false, type: 'dropdown', initialValue: null, options: [] },
      { controlName: 'driverBaseLocation', label: 'Driver Base Location', isReadonly: false, type: 'dropdown', initialValue: { name: 'Last Active Location' }, options: [{ name: 'Last Active Location' }, { name: 'Address Of Residence' }] },
      {
        controlName: 'activeDriversOnly',
        label: 'Active Drivers Only',
        hint: '(Assigned to ride today)',
        isReadonly: false,
        type: 'dropdown',
        initialValue: { name: 'No', value: false },
        options: [
          { name: 'Yes', value: true },
          { name: 'No', value: false },
        ],
      },
      {
        controlName: 'matchEquipment',
        label: 'Match Equipment',
        isReadonly: false,
        type: 'dropdown',
        initialValue: { name: 'No', value: false },
        options: [
          { name: 'Yes', value: true },
          { name: 'No', value: false },
        ],
      },
    ];

    this.advancedFiltersControls = [
      { controlName: 'distanceRadiusinmiles', label: 'Distance Radius In Miles', isReadonly: false, type: 'number', initialValue: 25, unitOfMeasure: 'miles' },
      { controlName: 'include2xBookedWithDifference', label: 'Include 2x Booked With Difference', isReadonly: false, type: 'number', initialValue: 0, unitOfMeasure: 'minutes' },
      {
        controlName: 'matchVehicle',
        label: 'Match Vehicle',
        isReadonly: false,
        type: 'dropdown',
        initialValue: { name: 'Yes', value: true },
        options: [
          { name: 'Yes', value: true },
          { name: 'No', value: false },
        ],
      },
      {
        controlName: 'bestMatchingDriversOnly',
        label: 'Best Matching Drivers Only',
        isReadonly: false,
        type: 'dropdown',
        initialValue: { name: 'No', value: false },
        options: [
          { name: 'Yes', value: true },
          { name: 'No', value: false },
        ],
      },
      {
        controlName: 'includeNonCompliantDrivers',
        label: 'Include Non Compliant Drivers',
        isReadonly: false,
        type: 'dropdown',
        initialValue: { name: 'No', value: false },
        options: [
          { name: 'Yes', value: true },
          { name: 'No', value: false },
        ],
      },
    ];
  }

  ngOnInit() {
    this.filtersForm = this.fb.group(this.initForm([...this.filtersControls, ...this.advancedFiltersControls]));

    this.getDriversData();

    this.columns = [
      { field: 'bestMatch', name: 'Best Match (%)', type: 'text' },
      { field: 'driver', name: 'Driver', type: 'object' },
      { field: 'mobileNumber', name: 'Mobile Number', type: 'text' },
      { field: 'assignmentCount', name: '# of Assignments', type: 'number' },
      { field: 'subassignmentCount', name: '# of Sub-Assignments', type: 'number' },
      { field: 'status', name: 'Status', type: 'text' },
    ];

    this.subdriverAssignedSub = this.actionDialogService.subdriverAssigned$.subscribe(res => {
      if (res) {
        this.ref.close();
      }
    });
  }

  hideDriverFromResults(driver: Driver) {
    this.filteredDrivers.update(prev => {
      const updatedDrivers = [...prev];
      const index = updatedDrivers.findIndex(d => d.partnerId == driver.partnerId);
      updatedDrivers.splice(index, 1);
      return updatedDrivers;
    });
  }

  initForm(controls: any[]): { [key: string]: any } {
    const group: { [key: string]: any } = {};
    controls.forEach(control => {
      group[control.controlName] = [{ value: control.initialValue, disabled: control.isReadonly }];
    });
    return group;
  }

  calcMatchPercent() {
    this.filteredDrivers.set(
      this.allDrivers().map((d: Driver) => {
        d.bestMatch = 100;
        d.moreInfo.reasons = [];
        this.checkEquipmentsMatch(d);
        this.checkVehicleMatch(d);
        this.checkComplianceMatch(d);
        this.checkDriverLocationMatch(d);
        this.checkDoublebookDurationMatch(d);
        this.checkPartnerAvailabilityMatch(d);

        d.status = d.bestMatch == 100 ? 'Available' : 'Attention';
        return d;
      }),
    );
    this.filterDrivers();
    this.filteredDrivers().sort((a, b) => b.bestMatch - a.bestMatch);
    this.first = 0;
  }

  filterDrivers() {
    this.filteredDriversMap.clear();
    this.filteredDrivers.set(
      this.allDrivers().filter(d => {
        if (this.filtersForm.value.driver?.name) {
          if (d.fullName != this.filtersForm.value.driver?.name) {
            return false;
          }
        }
        if (this.filtersForm.value.activeDriversOnly.value) {
          if (!d.isActiveDriver) {
            return false;
          }
        }
        if (!this.filtersForm.value.includeNonCompliantDrivers.value) {
          if (!d.isCompliant) {
            return false;
          }
        }
        if (this.filtersForm.value.bestMatchingDriversOnly.value) {
          if (d.bestMatch < 100) {
            return false;
          }
        }
        this.filteredDriversMap.set(d.partnerId, d);
        return true;
      }),
    );
  }

  checkEquipmentsMatch(d: Driver) {
    if (this.filtersForm.value.matchEquipment.value) {
      if (!this.config.data.rideEquipmentsIds.every((e: number) => d.equipmentIds?.includes(e))) {
        d.bestMatch -= 12.5;
        d.moreInfo.reasons.push({ name: `equipment`, details: `Missing Equipment.` });
      }
    }
  }
  checkVehicleMatch(d: Driver) {
    if (this.filtersForm.value.matchVehicle.value) {
      if (this.config.data.vehicleType?.toLowerCase() != d.vehicleType?.toLowerCase()) {
        d.bestMatch -= 12.5;
        d.moreInfo.reasons.push({ name: `vehicle`, details: `Vehicle type not matched.` });
      }
    }
  }
  checkComplianceMatch(d: Driver) {
    if (this.filtersForm.value.includeNonCompliantDrivers.value) {
      if (!d.isCompliant) {
        d.bestMatch -= 50;
        d.moreInfo.reasons.push({ name: `nonCompliant`, details: `Not compliant driver.` });
      }
    }
  }
  checkDriverLocationMatch(d: Driver) {
    const rideLocation = {
      latitude: this.config.data.rideLocationLat,
      longitude: this.config.data.rideLocationLong,
    };

    if (!this.filtersForm.value.distanceRadiusinmiles) {
      this.filtersForm.controls['distanceRadiusinmiles'].setValue(0);
    }

    const getLocationFromDB = () => {
      let driversLocation = {
        latitude: d.latitude ?? 0,
        longitude: d.longitude ?? 0,
      };
      let distance = this.calcDistance(driversLocation, rideLocation);

      if (distance > this.filtersForm.value.distanceRadiusinmiles) {
        d.bestMatch -= 12.5;
        d.moreInfo.reasons.push({ name: `distance`, details: `More than ${this.filtersForm.value.distanceRadiusinmiles} miles Far.` });
      }
    };

    if (this.filtersForm.value.distanceRadiusinmiles <= 0) {
      d.bestMatch -= 12.5;
      d.moreInfo.reasons.push({ name: `distance`, details: `More than ${this.filtersForm.value.distanceRadiusinmiles} miles Far.` });
      return;
    }

    if (this.filtersForm.value.driverBaseLocation.name == `Last Active Location`) {
      const firebaseDriverObj = this.driverFirestoreLocations().find(x => x.partner_id == d.partnerId);
      if (firebaseDriverObj?.latest_active_location) {
        let driversLocation = {
          latitude: firebaseDriverObj.latest_active_location._lat,
          longitude: firebaseDriverObj.latest_active_location._long,
        };

        let distance = this.calcDistance(driversLocation, rideLocation);
        d.distance = Number(distance);
        if (distance > this.filtersForm.value.distanceRadiusinmiles) {
          d.bestMatch -= 12.5;
          d.moreInfo.reasons.push({ name: `distance`, details: `More than ${this.filtersForm.value.distanceRadiusinmiles} miles Far.` });
        }
      } else {
        getLocationFromDB();
      }
    } else {
      getLocationFromDB();
    }
  }

  calcDistance(startCoords: coords, destCoords: coords) {
    const p1 = new google.maps.LatLng(startCoords.latitude, startCoords.longitude);
    const p2 = new google.maps.LatLng(destCoords.latitude, destCoords.longitude);
    return (google.maps.geometry.spherical.computeDistanceBetween(p1, p2) / 1000).toFixed(1);
  }

  checkDoublebookDurationMatch(d: Driver) {
    if (this.filtersForm.value.include2xBookedWithDifference > 0) {
      if (d.doubleBookedDuration > this.filtersForm.value.include2xBookedWithDifference) {
        d.bestMatch -= 12.5;
        d.moreInfo.reasons.push({ name: `duration`, details: `Late more than ${this.filtersForm.value.include2xBookedWithDifference} minutes.` });
      }
    }
  }

  checkPartnerAvailabilityMatch(d: Driver) {
    if (!d.availability) {
      d.bestMatch -= 12.5;
      d.moreInfo.reasons.push({ name: `availability`, details: `Driver is not online.` });
    }
  }

  getDriversData() {
    this.requestBuilder.get(`drivers/fulfillment/${this.config.data.state}`).subscribe({
      next: res => {
        console.log(res);

        this.allDrivers.set(
          res.data.map((d: Driver) => {
            this.driversMap.set(d.partnerId, d);
            this.filteredDriversMap.set(d.partnerId, d);
            return { ...d, bestMatch: 100, status: 'Available', moreInfo: { label: 'Attention needed', reasons: [] } };
          }),
        );
        this.filteredDrivers.set(this.allDrivers());

        this.allDriversOptions = this.allDrivers().map(d => {
          const cameraStatus = this.cameraStatusService.getCurrentStatus(d.partnerId);
          // Subscribe all drivers for live camera updates
          const sub = this.cameraStatusService.watchStatus(d.partnerId).subscribe((status: CameraStatus) => {
            console.log(status);

            if (d.partnerId == 251479) {
              console.log(d, status);
            }
            this.updateDriverCameraStatus(d.partnerId, status);
          });
          this.cameraSubscriptions.push(sub);
          return { name: d.fullName, partnerId: d.partnerId, mobileNumber: d.mobileNumber ?? undefined, organization: d.organization, cameraStatus };
        });
        this.driversOptions.set([...this.allDriversOptions]);

        this.listenToPartnerChanges();

        this.totalRecords = this.allDrivers().length;
        this.calcMatchPercent();
      },
      error: error => {},
    });
  }

  updateDriverCameraStatus(partnerId: number, status: CameraStatus) {
    const driverIdx = this.allDriversOptions.findIndex(d => d.partnerId === partnerId);
    if (driverIdx !== -1) {
      const oldDriver = this.allDriversOptions[driverIdx];
      const updatedDriver = { ...oldDriver, cameraStatus: status };
      this.allDriversOptions[driverIdx] = updatedDriver;

      // Update filtered drivers incrementally - O(1) operation
      this.updateFilteredDriversIncrementally(oldDriver, updatedDriver);
    }
  }

  private updateFilteredDriversIncrementally(oldDriver: DriverOptionsData, updatedDriver: DriverOptionsData) {
    const updatedFiltered = CameraFilterUtils.updateFilteredDriversIncrementally(this.driversOptions(), oldDriver, updatedDriver, this.currentFilterCode, this.allDriversOptions);
    this.driversOptions.set(updatedFiltered);
  }

  listenToPartnerChanges() {
    if (this.stateService.getFirebaseUser()) {
      if (this.sub) {
        this.sub.unsubscribe();
      }
      this.sub = this.firestoreService.listenToCollectionChanges(this.env.getConfig().partnerCollection).subscribe({
        next: res => {
          const { changes } = res;

          if (this.firestoreDriversInitialLoad) {
            this.driverFirestoreLocations.set(changes.map((item: { id: string; data: DriverDocument; type: string }) => item.data));
            this.calcMatchPercent();
            this.firestoreDriversInitialLoad = false;
          }

          // Handle realtime updates
          changes.forEach((c: { docId: string; data: DriverDocument; type: string; doc: DocumentData }) => {
            if (c.type == ActionType.added && !this.firestoreDriversInitialLoad) {
              this.driverFirestoreLocations.update(prev => {
                return [...prev, c.data];
              });
            }
            if (c.type == ActionType.modified) {
              this.updatedDriverLocation.set(c.data);
            }
          });
        },
        error: error => {},
      });
    }
  }

  toggleAdvancedFilters() {
    this.showAdvancedFilters = !this.showAdvancedFilters;
  }

  onPage(event: any) {
    this.first = event.first;
    this.rows = event.rows;
  }

  onRowsPerPageChange(event: any) {
    this.rows = event.value;
    this.first = 0;
    this.table()?.reset();
  }

  getPaginatedData(first: number, rows: number) {
    return this.allDrivers().slice(first, first + rows);
  }

  openAssignSubDriverDialog(selectedDriverId: number) {
    if (this.currentRole == UserRole.Provider) {
      this.rideActionsService.openProviderAssignSubDriverDialog(this.config.data.ride, this.config.data.docRide, this.dialogService, this.messageService, selectedDriverId);
    } else {
      this.rideActionsService.openAssignSubDriverDialog(this.config.data.ride, this.config.data.docRide, this.dialogService, this.messageService, selectedDriverId);
    }
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
    this.subdriverAssignedSub.unsubscribe();
    this.cameraSubscriptions.forEach(s => s.unsubscribe());
  }
}
